import { roundedClassname } from '@/helpers/environmentHelper';
import { useAppDispatch, useAppSelector } from '@/state/hooks';
import {
    selectCredentials,
    selectCurrentAccountName,
    selectCurrentEntityName,
    setCurrentAccount,
    setCurrentAccountName,
    setCurrentEntityName
} from '@/state/reducers/authSlice';
import {
    openNotifications,
    selectNotificationsOpened,
    setNotificationsVisited
} from '@/state/reducers/notificationSlice';
import useLanguage from '@/utils/hooks/useLanguage';
import { navigateAccordingToAccountType } from '@/utils/navigation';
import { SSOLoadingState, useSso } from '@/utils/providers/SSOProvider';
import { Menu } from '@headlessui/react';
import { default as cn } from 'classnames';
import { ButtonHTMLAttributes, useMemo } from 'react';
import { IconType } from 'react-icons/lib';
import { MdExpandMore, MdOutlineAccountBox, MdOutlineAccountCircle, MdSwitchAccount } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { DropDown } from '../common/Dropdown';
import LanguageButton from '../common/LanguageButton';
import NotificationButton from '../common/NotificationButton';
import { Ping } from './MobileDrawer';
import { onUserChange } from './Navbar';

export const ssoDisabledMessage = (isLoadingSSO: SSOLoadingState) => {
    if (isLoadingSSO === 'pending') return 'We are currently connecting this feature via SSO...';
    if (isLoadingSSO === 'error') return 'This feature is not available due to an error in SSO.';
    return 'This feature requires SSO to be enabled.';
};

interface DesktopNavbarProps {
    isPortal?: boolean;
}

const DesktopNavbar = ({ isPortal = false }: DesktopNavbarProps) => {
    const dispatch = useAppDispatch();

    const lang = useLanguage();
    const credentials = useAppSelector(selectCredentials);
    const currentAccountName = useAppSelector(selectCurrentAccountName);
    const notificationsOpened = useAppSelector(selectNotificationsOpened);
    const currentEntityName = useAppSelector(selectCurrentEntityName);

    const { entities, flattenEntities } = useSso();

    const navigate = useNavigate();

    const convertedAccounts = useMemo(() => {
        return (
            credentials?.accounts?.map((account) => ({
                code: account.code,
                name: account.name,
                properties: account.properties,
                entityName: entities?.find(
                    (entity) =>
                        entity.nameMappings?.find((nameMapping) => nameMapping.source === 'CELER')?.name ===
                        account.name
                )?.entityName
            })) || []
        );
    }, [credentials, entities]);

    const gcexAccountName = useMemo(() => {
        if (entities && !currentEntityName) {
            return (
                entities.find(
                    (entity) =>
                        entity.nameMappings?.find((nameMapping) => nameMapping.source === 'CELER')?.name ===
                        currentAccountName
                )?.entityName || currentAccountName
            );
        } else return currentAccountName;
    }, [entities, currentAccountName, currentEntityName]);

    return (
        <div className="hidden lg:flex flex-row h-full items-center gap-3 py-2">
            <LanguageButton />
            <NotificationButton
                size="w-5 h-5"
                isActive={notificationsOpened}
                onClick={() => {
                    dispatch(setNotificationsVisited(true));
                    dispatch(openNotifications());
                }}
            />

            <DropDown
                label={lang.common.switchAccount}
                button={<MenuButton label={currentEntityName || gcexAccountName} Icon={MdOutlineAccountBox} />}>
                {convertedAccounts.map((account, i) => {
                    const disabled = !flattenEntities?.find((entity) =>
                        entity.nameMappings?.find((name) => name.source === 'CELER' && name.name === account.name)
                    );
                    return (
                        <MenuItemButton
                            key={i}
                            label={account.entityName || account.name}
                            Icon={MdSwitchAccount}
                            onClick={() => {
                                dispatch(setCurrentAccount(account.code));
                                dispatch(setCurrentAccountName(account.name));
                                dispatch(setCurrentEntityName(account.entityName));
                                onUserChange(credentials, dispatch);
                                navigateAccordingToAccountType(account, navigate);
                            }}
                            disabled={disabled && isPortal}
                        />
                    );
                })}
            </DropDown>
            <LoggedInUser />
        </div>
    );
};

export default DesktopNavbar;

interface MenuButtonProps {
    Icon: IconType;
    label?: string;
}

export const MenuButton = (props: MenuButtonProps) => {
    const { Icon, label } = props;

    return (
        <Menu.Button
            className={cn(
                'flex w-56 gap-2 text-sm items-center px-2 p-1 justify-between text-brand-text bg-brand-background-dark',
                roundedClassname
            )}>
            <span>
                <Icon className="h-5 w-5" />
            </span>
            <div className="truncate whitespace-nowrap">{label}</div>
            <span className="ml-auto">
                <MdExpandMore className="h-5 w-5" />
            </span>
        </Menu.Button>
    );
};

interface MenuItemButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    Icon?: IconType;
    icon?: any;
    label: string;
    disabledMessage?: string;
    isLoadingSSO?: SSOLoadingState;
    requiresSSO?: boolean;
}

export const MenuItemButton = (props: MenuItemButtonProps) => {
    const { icon, Icon, label, disabledMessage, isLoadingSSO = 'idle', requiresSSO = false, ...restProps } = props;
    const { disabled } = restProps;

    const ssoTooltip = useMemo(() => {
        if (isLoadingSSO === 'pending') return 'We are currently connecting this feature via SSO...';
        if (isLoadingSSO === 'error') return 'This feature is not available due to an error in SSO.';
        return '';
    }, [isLoadingSSO]);

    const tooltip = useMemo(() => {
        if (disabled) return ssoTooltip || disabledMessage;
        return ssoTooltip;
    }, [disabled, disabledMessage, ssoTooltip]);

    return (
        <Menu.Item>
            <button
                type="button"
                title={tooltip}
                className={cn('group flex w-full gap-2 items-center p-2 text-sm bg-brand-background-dark', {
                    'text-brand-neutral-400 cursor-not-allowed': disabled,
                    'text-brand-text hover:bg-brand-primary hover:text-brand-module-title': !disabled
                })}
                {...restProps}>
                {Icon ? (
                    <span>
                        <Icon className="h-5 w-5" />
                    </span>
                ) : null}
                {icon ? <span>{icon}</span> : null}
                <div className="relative flex items-center justify-center">
                    {isLoadingSSO === 'pending' && requiresSSO && <Ping isLoadingSSO={isLoadingSSO} />}
                    {isLoadingSSO === 'error' && requiresSSO && <Ping isLoadingSSO={isLoadingSSO} />}
                    <div
                        className={cn('truncate whitespace-nowrap', {
                            'pl-4': requiresSSO && ['pending', 'error'].includes(isLoadingSSO)
                        })}>
                        {label}
                    </div>
                </div>
            </button>
        </Menu.Item>
    );
};

export const LoggedInUser = () => {
    const lang = useLanguage();
    const credentials = useAppSelector(selectCredentials);
    return (
        <div className={cn('relative inline-block text-left border border-brand-neutral-700', roundedClassname)}>
            <div className="relative flex items-center">
                <span className="absolute -top-2 left-2 text-2xs text-brand-neutral-400 font-bold">
                    {lang.common.user}
                </span>

                <div
                    className={cn(
                        'flex w-48 gap-2 text-sm items-center px-2 p-1 text-brand-text bg-brand-background-dark',
                        roundedClassname
                    )}>
                    <span>
                        <MdOutlineAccountCircle className="h-5 w-5" />
                    </span>
                    <div className="truncate whitespace-nowrap">{credentials?.username}</div>
                </div>
            </div>
        </div>
    );
};
